import { Component, AfterViewInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FounderBannerComponent } from '../founder-banner/founder-banner.component';
import { DemoStoryboardComponent } from '../demo-storyboard/demo-storyboard.component';
import { VideoPlayerComponent } from '../video-player/video-player.component';

// Update procedure: docs/FEATURE_VIDEO_PRESENTATION.md
// Versioned file names: assets are served with a 1-year immutable cache (Caddy).
// ngsw-bypass: the Angular service worker does not handle video Range requests (Safari/iOS).
const DEMO_VIDEO = {
  src: '/assets/videos/presentation-720p-v1.mp4?ngsw-bypass=true',
  poster: '/assets/videos/presentation-poster-v1.webp',
  label: 'Vidéo de présentation de la solution',
  captions: [
    {
      lang: 'fr',
      label: 'Français',
      src: '/assets/videos/presentation-v1.fr.vtt',
    },
  ],
} as const;

@Component({
  selector: 'app-landing-v2',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FounderBannerComponent,
    DemoStoryboardComponent,
    VideoPlayerComponent,
  ],
  templateUrl: './landing-v2.component.html',
  styleUrls: ['./landing-v2.component.css'],
})
export class LandingV2Component implements AfterViewInit {
  /** Toggle hero mode: 'itinerant' | 'fixed' */
  mode = signal<'itinerant' | 'fixed'>('itinerant');

  toggleMode(m: 'itinerant' | 'fixed') {
    this.mode.set(m);
  }

  /** Demo video state: falls back to the interactive storyboard if it fails */
  readonly demoVideo = DEMO_VIDEO;
  readonly demoVideoFailed = signal(false);

  onDemoVideoError(): void {
    this.demoVideoFailed.set(true);
  }

  /** FAQ accordion state */
  openFaq = signal<number | null>(null);

  toggleFaq(i: number) {
    this.openFaq.set(this.openFaq() === i ? null : i);
  }

  faqs = [
    {
      q: 'Comment ça marche concrètement ?',
      a: 'Vous décrivez votre journée (lieu, date, horaires) et notre IA analyse la météo, votre historique de ventes et le contexte local pour vous recommander les quantités optimales de chaque produit. En 2 minutes, votre liste de préparation est prête.',
    },
    {
      q: 'Ça marche pour mon type de commerce ?',
      a: "Oui. Predivent couvre 13 types d'activité : food trucks, boulangeries, sandwicheries, crêperies, bars, primeurs, glaciers, traiteurs, stands marchés... Le système s'adapte automatiquement à chaque métier grâce à des modèles spécialisés.",
    },
    {
      q: "Et si je n'ai aucun historique ?",
      a: "Pas de problème. Au démarrage, Predivent utilise les données agrégées de commerces similaires au vôtre (même type, même zone). Dès votre 1ère journée de vente saisie, l'IA commence à se personnaliser.",
    },
    {
      q: "L'essai gratuit est-il vraiment sans carte bancaire ?",
      a: "Oui, 1 mois d'essai complet, toutes les fonctionnalités, sans limite et sans carte bancaire. L'essai n'est pas renouvelable. Vous décidez ensuite, et un abonnement peut être mis en pause sans perdre vos données.",
    },
    {
      q: "Comment l'IA s'améliore-t-elle ?",
      a: "Après chaque journée, vous saisissez vos ventes réelles. Le système compare ses prévisions à la réalité et affine ses calculs. Plus vous l'utilisez, plus c'est précis — nos meilleurs utilisateurs atteignent 92% de précision.",
    },
    {
      q: 'Puis-je annuler facilement ?',
      a: 'Résiliation en 1 clic depuis les paramètres, sans frais cachés, sans engagement. Vos données restent exportables pendant 30 jours après résiliation.',
    },
  ];

  ngAfterViewInit(): void {
    this.initScrollAnimations();
    this.initSmoothScroll();
    this.initCountUp();
  }

  private initScrollAnimations() {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' },
    );
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
  }

  private initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const id = (anchor as HTMLAnchorElement).getAttribute('href');
        if (!id) return;
        const target = document.querySelector(id);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  private initCountUp() {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const end = parseInt(el.dataset['count'] || '0', 10);
            const suffix = el.dataset['suffix'] || '';
            const prefix = el.dataset['prefix'] || '';
            this.animateCount(el, end, prefix, suffix);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.5 },
    );
    document
      .querySelectorAll('.count-up')
      .forEach((el) => observer.observe(el));
  }

  private animateCount(
    el: HTMLElement,
    end: number,
    prefix: string,
    suffix: string,
  ) {
    const duration = 1500;
    const start = 0;
    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(start + (end - start) * eased);
      el.textContent = `${prefix}${current}${suffix}`;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}
